import { useSearchParams } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"

import { v4CustomersGeocode } from "@be-miniapp/api"
import type { GeocodeV4Response } from "@be-miniapp/api"
import { useEffectOnce } from "@be-miniapp/ui/hooks/use-effect-once"

import {
  selectRideBookingActions,
  selectRideRouteSelectionActions,
} from "../stores/ride-data.selectors"
import type { RideDataStoreHook } from "./use-fare-page-model"

export interface FareUrlGeocodeFields {
  name: string
  address: string
  placeId: string
  location: { latitude: number; longitude: number }
}

/**
 * Reads pickup/dropoff latlng query params, geocodes once, and commits to the ride store.
 * Apps supply `mapGeocodeToStandardLocation` (may differ slightly by platform geocode shape).
 */
export function useFareUrlParams(deps: {
  useRideDataStore: RideDataStoreHook
  mapGeocodeToStandardLocation: (geo: GeocodeV4Response) => FareUrlGeocodeFields
}) {
  const { useRideDataStore, mapGeocodeToStandardLocation } = deps
  const [searchParams] = useSearchParams()
  const { setPickup, setDropoff } = useRideDataStore(
    useShallow(selectRideRouteSelectionActions)
  )
  const { setVehicleType } = useRideDataStore(
    useShallow(selectRideBookingActions)
  )

  useEffectOnce(() => {
    void (async () => {
      const pickupLocation =
        searchParams.get("pickup") || searchParams.get("pickup_latlong")
      const dropoffLocation =
        searchParams.get("dropoff") || searchParams.get("dropoff_latlong")
      const vehicleType =
        (
          searchParams.get("selected_vehicles") ??
          searchParams.get("selected_vehicle")
        )?.split(",") || []

      if (vehicleType[0]) {
        setVehicleType(Number(vehicleType[0]))
      }

      if (pickupLocation && dropoffLocation) {
        const [lat, lng] = pickupLocation.split(",")
        const [dropoffLat, dropoffLng] = dropoffLocation.split(",")

        const pickUp = await v4CustomersGeocode({
          latitude: Number(lat),
          longitude: Number(lng),
          location: {
            latitude: Number(lat),
            longitude: Number(lng),
          },
        })

        const dropOff = await v4CustomersGeocode({
          latitude: Number(dropoffLat),
          longitude: Number(dropoffLng),
          location: {
            latitude: Number(dropoffLat),
            longitude: Number(dropoffLng),
          },
        })

        const pickupLoc = mapGeocodeToStandardLocation(pickUp)
        const dropoffLoc = mapGeocodeToStandardLocation(dropOff)

        setPickup({
          ...pickupLoc,
          type: "pick-up",
          isConfirmed: true,
        })
        setDropoff({
          ...dropoffLoc,
          type: "drop-off",
          isConfirmed: true,
        })
      }
    })()
  })
}
